import axios, {
  AxiosError,
  type AxiosRequestHeaders,
  type InternalAxiosRequestConfig,
} from 'axios';

import { useShowToast } from '@/composables/useShowToast.composable';
import { AuthErrorCodesEnum } from '@/helpers/enums/authError.enum';
import { EnumRoutes } from '@/helpers/enums/routes.enum';
import { pinia } from '@/pinia';
import router from '@/router';
import { useAuthStore } from '@/stores/auth.store';
import { useLocaleStore } from '@/stores/locale.store';
import { usePermissionsStore } from '@/stores/permissoins.store';
interface AxiosResponseData {
  code?: string;
  errorCode?: string;
  explanation?: string;
  message?: string;
  statusCode?: number;
  time?: string;
}
const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_APP_API_BASE_URL,
});

axiosInstance.interceptors.request.use(
  (config) => {
    const localStore = useLocaleStore(pinia);
    if (!config) {
      config = {} as InternalAxiosRequestConfig<any>;
    }
    if (!config.headers) {
      config.headers = {} as AxiosRequestHeaders;
    }
    config.headers['language'] = `${localStore.locale}`;
    config.headers['Organization'] = localStorage.getItem('Organization') || 1;

    const token = localStorage.getItem('access');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    return config;
  },

  (error) => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },

  async (error: any) => {
    if (!axios.isAxiosError(error)) {
      return Promise.reject<AxiosError>(error);
    }

    const authStore = useAuthStore();
    const permissionsStore = usePermissionsStore();
    // const { status } = error.response || {};
    const errorCode = (error.response?.data as AxiosResponseData).errorCode;
    if (errorCode) {
      switch (errorCode) {
        case AuthErrorCodesEnum.UserIsDisabled:
          // disabled user, logout if there is a user only
          if (authStore.user) {
            await authStore.handleLogout();
            permissionsStore.checkAuthentication(
              router.currentRoute.value.meta,
              true,
              router.currentRoute.value.fullPath
            );
            useShowToast.error({
              msg: 'error.disabledUser',
              timeout: 5000,
              translate: true,
            });
          }
          break;
        case AuthErrorCodesEnum.UserNotInOrganization:
          // userNotInOrganization
          break;
        case AuthErrorCodesEnum.InvalidCredentials:
          // UserNotFound
          break;
        case AuthErrorCodesEnum.TooManyRequests:
          // tooRequest
          break;
        case AuthErrorCodesEnum.UserShouldResetPassword:
          authStore.verifyToken(true);
          useShowToast.error({
            msg: 'error.userShouldResetPassword',
            timeout: 5000,
            translate: true,
          });
          break;
        case AuthErrorCodesEnum.InvalidAccessToken: {
          // InvalidAccessToken
          // Don't logout if:
          // 1. The error came from verify-email or report-email endpoints
          //    (they use their own JWT tokens, not the user's access token)
          // 2. The user is currently on the verify-email or report-email page
          //    (background calls like verifyToken/generateSSO may fail but
          //    shouldn't disrupt the verification flow)
          const requestUrl = error.config?.url || '';
          const currentPath = router.currentRoute.value.path || '';
          if (
            requestUrl.includes('verify-email') ||
            requestUrl.includes('report-email') ||
            currentPath.includes('verify-email') ||
            currentPath.includes('report-email')
          ) {
            break;
          }
          // logout out manually to avoid infinite loop if logout API is used
          await authStore.handleLogout();
          permissionsStore.checkAuthentication(
            router.currentRoute.value.meta,
            true,
            router.currentRoute.value.fullPath
          );
          break;
        }
        case AuthErrorCodesEnum.InvalidRefreshToken:
          // InvalidRefreshToken
          await authStore.logout();
          permissionsStore.checkAuthentication(
            router.currentRoute.value.meta,
            true,
            router.currentRoute.value.fullPath
          );
          break;
        case AuthErrorCodesEnum.Invalid2FAToken:
          // Invalid2FAToken
          break;
        case AuthErrorCodesEnum.NotVerified: {
          // User is not verified — redirect to verify email page
          // but not if already on a verify-email or report-email related page
          const currentPath = router.currentRoute.value.path || '';
          if (
            authStore.user &&
            !currentPath.includes('verify-email') &&
            !currentPath.includes('report-email')
          ) {
            await router.replace({ name: EnumRoutes.auth.verifyEmail.name });
          }
          break;
        }
        case AuthErrorCodesEnum.RequiresApproval:
          // User requires approval — logout
          if (authStore.user) {
            await authStore.handleLogout();
          }
          break;
        case AuthErrorCodesEnum.EmailVerificationInvalid:
        case AuthErrorCodesEnum.EmailVerificationRequestMissing:
        case AuthErrorCodesEnum.EmailVerificationOrganizationRequired:
          // Email verification errors — pass through to component
          // Don't logout or redirect, let the verify-email/report-email page handle it
          break;
      }
    }
    return Promise.reject<AxiosError>(error);
  }
);

export default axiosInstance;
